import { Ref, computed, watch } from "@vue/composition-api";
import { STOP_AT, SensitivityStop, state } from "./state";

/**
 * A Visibility card's slider stop, kept loaded (W3, W4, W5). Each stop is its
 * own query (state.ts, "The Visibility cards' slider stops"): moving the
 * slider asks for the new stop the first time, and a new period asks again
 * once the dashboard has loaded. The answer is one count per category at that
 * stop — not split by sensitivity — so a bar is one segment.
 */

/** how a stop reads in tooltips and accessible names */
export const STOP_TEXT: Record<SensitivityStop, string> = {
  high: "High sensitivity",
  medium: "Medium sensitivity or higher",
  low: "Low sensitivity or higher",
  all: "Any content",
};

const use_stop = <T>(
  level: Ref<number>,
  source: () => {
    data: Partial<Record<SensitivityStop, T>>;
    loading: Partial<Record<SensitivityStop, boolean>>;
    failed: Partial<Record<SensitivityStop, boolean>>;
  },
  load: (stop: SensitivityStop) => Promise<void>
) => {
  const stop = computed<SensitivityStop>(() => STOP_AT[level.value - 1] || "all");
  watch(
    () => [stop.value, state.loading.value.is_loading],
    () => {
      if (!state.loading.value.is_loading) load(stop.value);
    }
  );
  const data = computed(() => source().data[stop.value] || null);
  const failed = computed(() => !!source().failed[stop.value]);
  /** asked for and not back yet — or not asked for yet */
  const loading = computed(() => !data.value && !failed.value);
  return { stop, data, failed, loading };
};

export const use_visibility_stop = (level: Ref<number>) =>
  use_stop(level, () => state.visibility.value, state.load_visibility_stop);

export const use_people_stop = (level: Ref<number>) =>
  use_stop(level, () => state.people.value, state.load_people_stop);
